class SaintsCampPage {
  constructor(root) {
    this.root = root;
    this.items = [...root.querySelectorAll('[data-camp-category]')];
    this.filterButtons = [...root.querySelectorAll('[data-camp-filter]')];
    this.batchSize = 13;
    this.visibleCount = this.batchSize;
    this.activeFilter = 'reporter';
    this.selectedReporter = '';
    this.selectedPosition = '';
    this.selectedVideoSource = '';
    this.selectedSaintsCcSource = '';
    this.saintsCcAllChannelLimit = 0;
    this.loadButton = root.querySelector('[data-camp-load-more]');
    this.reporterDropdown = root.querySelector('[data-camp-reporter-dropdown]');
    this.reporterDropdownMenu = root.querySelector('[data-camp-reporter-dropdown-menu]');
    this.reporterDropdownButton = root.querySelector('[data-camp-reporter-dropdown-trigger]');
    this.positionDropdown = root.querySelector('[data-camp-position-dropdown]');
    this.positionDropdownMenu = root.querySelector('[data-camp-position-dropdown-menu]');
    this.positionDropdownButton = root.querySelector('[data-camp-position-dropdown-trigger]');
    this.videoDropdown = root.querySelector('[data-camp-video-dropdown]');
    this.videoDropdownMenu = root.querySelector('[data-camp-video-dropdown-menu]');
    this.videoDropdownButton = root.querySelector('[data-camp-video-dropdown-trigger]');
    this.saintsCcDropdown = root.querySelector('[data-camp-saints-cc-dropdown]');
    this.saintsCcDropdownMenu = root.querySelector('[data-camp-saints-cc-dropdown-menu]');
    this.saintsCcDropdownButton = root.querySelector('[data-camp-saints-cc-dropdown-trigger]');
    const feedSection = root.querySelector('[data-camp-feed-api]');
    this.feedApiUrl = feedSection?.dataset.campFeedApi?.replace(/\/$/, '');
    const requestedAdInterval = Number.parseInt(feedSection?.dataset.campAdInterval || '4', 10);
    this.adInterval = Number.isFinite(requestedAdInterval)
      ? Math.max(1, requestedAdInterval)
      : 4;
    this.adTemplates = [...root.querySelectorAll('[data-camp-ad-template]')];
    this.reporterAutoRefreshEnabled = this.shouldEnableReporterAutoRefresh();
    this.xMetricIcons = {
      comments: feedSection?.dataset.campCommentIcon,
      reposts: feedSection?.dataset.campRepostIcon,
      likes: feedSection?.dataset.campLikeIcon
    };
    this.bind();
    this.updateFilterControls();
    this.render();
    this.setupAutoLoadMore();
    this.loadReporterFeeds();
    if (this.reporterAutoRefreshEnabled) this.startReporterAutoRefresh();
    this.loadCampVideos();
    this.loadSaintsCcVideos();
    this.loadCampBuzz();
    this.loadCampToday();
    this.loadCampPhotos();
    this.loadSaintsRoster();
    this.startCountdowns();
    this.setupStickyTabs();
    this.setupLiveAlertSignup();
  }

  bind() {
    this.filterButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.activeFilter = button.dataset.campFilter || 'all';
        if (this.activeFilter === 'reporter') this.selectedReporter = '';
        if (this.activeFilter === 'player') this.selectedPosition = '';
        if (this.activeFilter === 'video') this.selectedVideoSource = '';
        if (this.activeFilter === 'saints-cc') this.selectedSaintsCcSource = '';
        this.visibleCount = this.batchSize;
        this.updateFilterControls();
        this.render();
        if (this.isXFeedFilter() && this.reporterAutoRefreshEnabled) {
          this.loadReporterFeeds();
        }
        this.root.querySelector('#camp-feed')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
    this.loadButton?.addEventListener('click', () => {
      this.visibleCount += this.batchSize;
      this.render();
    });
    this.root.querySelectorAll('[data-camp-archive-button]').forEach((button) => {
      button.addEventListener('click', () => this.toggleReporterArchive(button));
    });
    this.reporterDropdownButton?.addEventListener('click', () => this.toggleReporterDropdown());
    this.reporterDropdown?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeReporterDropdown();
        this.reporterDropdownButton?.focus({ preventScroll: true });
      }
    });
    document.addEventListener('click', (event) => {
      if (this.reporterDropdown && !this.reporterDropdown.contains(event.target)) {
        this.closeReporterDropdown();
      }
    });
    this.root.querySelectorAll('[data-camp-reporter-choice]').forEach((button) => {
      button.addEventListener('click', () => this.selectReporter(button.dataset.campReporterChoice));
    });
    this.positionDropdownButton?.addEventListener('click', () => this.togglePositionDropdown());
    this.positionDropdown?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closePositionDropdown();
        this.positionDropdownButton?.focus({ preventScroll: true });
      }
    });
    this.positionDropdownMenu?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-camp-position-choice]');
      if (button) this.selectPosition(button.dataset.campPositionChoice);
    });
    document.addEventListener('click', (event) => {
      if (this.positionDropdown && !this.positionDropdown.contains(event.target)) {
        this.closePositionDropdown();
      }
    });
    this.videoDropdownButton?.addEventListener('click', () => this.toggleVideoDropdown());
    this.videoDropdown?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeVideoDropdown();
        this.videoDropdownButton?.focus({ preventScroll: true });
      }
    });
    this.videoDropdownMenu?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-camp-video-choice]');
      if (button) this.selectVideoSource(button.dataset.campVideoChoice);
    });
    document.addEventListener('click', (event) => {
      if (this.videoDropdown && !this.videoDropdown.contains(event.target)) {
        this.closeVideoDropdown();
      }
    });
    this.saintsCcDropdownButton?.addEventListener('click', () => this.toggleSaintsCcDropdown());
    this.saintsCcDropdown?.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeSaintsCcDropdown();
        this.saintsCcDropdownButton?.focus({ preventScroll: true });
      }
    });
    this.saintsCcDropdownMenu?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-camp-saints-cc-choice]');
      if (button) this.selectSaintsCcSource(button.dataset.campSaintsCcChoice);
    });
    document.addEventListener('click', (event) => {
      if (this.saintsCcDropdown && !this.saintsCcDropdown.contains(event.target)) {
        this.closeSaintsCcDropdown();
      }
    });
  }

  async setupLiveAlertSignup() {
    const panel = this.root.querySelector('[data-camp-live-alert]');
    const form = panel?.querySelector('[data-camp-live-alert-form]');
    const status = panel?.querySelector('[data-camp-live-alert-status]');
    if (!panel || !form || !status || !this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/live-alerts/status`, {
        headers: { Accept: 'application/json' },
        credentials: 'omit'
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.available) return;
      panel.hidden = false;
    } catch (error) {
      console.warn('Live X email alert signup is unavailable', error);
      return;
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;
      const button = form.querySelector('button[type="submit"]');
      const formData = new FormData(form);
      const originalLabel = button?.textContent;
      status.className = 'camp-live-alert__status';
      status.textContent = 'Sending confirmation…';
      if (button) {
        button.disabled = true;
        button.textContent = 'Please wait…';
      }

      try {
        const response = await fetch(`${this.feedApiUrl}/api/live-alerts/subscribe`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify({
            email: formData.get('email'),
            company: formData.get('company'),
            consent: formData.get('consent') === 'on'
          })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Unable to subscribe right now.');
        status.classList.add('camp-live-alert__status--success');
        status.textContent = payload.message || 'Check your inbox to confirm your alert.';
        form.reset();
      } catch (error) {
        status.classList.add('camp-live-alert__status--error');
        status.textContent = error.message || 'Unable to subscribe right now.';
      } finally {
        if (button) {
          button.disabled = false;
          button.textContent = originalLabel;
        }
      }
    });
  }

  render() {
    let matched = 0;
    this.items.forEach((item) => {
      const categories = (item.dataset.campCategory || '').split(' ');
      const categoryMatches = this.activeFilter === 'all' || categories.includes(this.activeFilter);
      const reporterMatches =
        this.activeFilter !== 'reporter' ||
        !this.selectedReporter ||
        item.dataset.campReporterHandle === this.selectedReporter;
      const positionMatches =
        this.activeFilter !== 'player' ||
        !this.selectedPosition ||
        item.hasAttribute('data-camp-roster-source') ||
        item.dataset.campPlayerPosition === this.selectedPosition;
      const videoMatches =
        this.activeFilter !== 'video' ||
        !this.selectedVideoSource ||
        item.dataset.campVideoSource === this.selectedVideoSource;
      const saintsCcMatches =
        this.activeFilter !== 'saints-cc' ||
        !this.selectedSaintsCcSource ||
        item.dataset.campVideoSource === this.selectedSaintsCcSource;
      const isMatch =
        categoryMatches && reporterMatches && positionMatches && videoMatches && saintsCcMatches;
      matched += isMatch ? 1 : 0;
      const displayLimit =
        this.activeFilter === 'saints-cc'
          ? this.selectedSaintsCcSource
            ? 3
            : this.saintsCcAllChannelLimit || 9
          : this.visibleCount;
      item.hidden = !isMatch || matched > displayLimit;
    });
    if (this.loadButton) {
      const canLoadMore = this.activeFilter !== 'saints-cc' && matched > this.visibleCount;
      this.loadButton.hidden = !canLoadMore;
      this.loadButton.setAttribute('aria-expanded', String(!canLoadMore));
    }
    this.syncInstreamAds();
  }

  syncInstreamAds() {
    this.root.querySelectorAll('[data-camp-instream-ad]').forEach((ad) => ad.remove());
    if (!this.isXFeedFilter() || !this.adTemplates.length) return;

    const visiblePosts = this.items.filter(
      (item) =>
        item.matches('.camp-feed-card--x') &&
        !item.hidden &&
        Boolean(item.dataset.campPublishedAt)
    );
    const effectiveInterval = this.activeFilter === 'cowboys' ? 3 : this.adInterval;

    visiblePosts.forEach((post, index) => {
      if (!effectiveInterval || (index + 1) % effectiveInterval !== 0) return;
      const adIndex = Math.floor(index / effectiveInterval) % this.adTemplates.length;
      const ad = this.adTemplates[adIndex].content.firstElementChild?.cloneNode(true);
      if (ad) post.insertAdjacentElement('afterend', ad);
    });
  }

  updateFilterControls() {
    const labels = {
      reporter: 'Live X Updates From Saints Camp',
      cowboys: 'Saints Opponent Live X Post Updates',
      video: 'Latest Videos from Camp',
      'saints-cc': 'Latest Videos from Saints Content Creators',
      photo: 'Latest Photos from Camp',
      player: 'Current Roster',
      all: 'Latest coverage from Saints Camp'
    };
    this.filterButtons.forEach((item) =>
      item.setAttribute('aria-pressed', String(item.dataset.campFilter === this.activeFilter))
    );
    this.root.querySelectorAll('[data-camp-reporter-choice]').forEach((item) => {
      item.setAttribute(
        'aria-pressed',
        String((item.dataset.campReporterChoice || '') === this.selectedReporter)
      );
    });
    if (this.reporterDropdownButton) {
      this.reporterDropdownButton.classList.toggle('is-selected', Boolean(this.selectedReporter));
    }
    const selectedChoice = [...this.root.querySelectorAll('[data-camp-reporter-choice]')].find(
      (item) => (item.dataset.campReporterChoice || '') === this.selectedReporter
    );
    const dropdownLabel = this.root.querySelector('[data-camp-reporter-dropdown-label]');
    if (dropdownLabel) {
      dropdownLabel.textContent = selectedChoice?.dataset.campReporterName || 'All Reporters';
    }
    this.root.querySelectorAll('[data-camp-position-choice]').forEach((item) => {
      item.setAttribute(
        'aria-pressed',
        String((item.dataset.campPositionChoice || '') === this.selectedPosition)
      );
    });
    const selectedPositionChoice = [
      ...this.root.querySelectorAll('[data-camp-position-choice]')
    ].find((item) => (item.dataset.campPositionChoice || '') === this.selectedPosition);
    const positionLabel = this.root.querySelector('[data-camp-position-dropdown-label]');
    if (positionLabel) {
      positionLabel.textContent =
        selectedPositionChoice?.dataset.campPositionName || 'All Positions';
    }
    this.root.querySelectorAll('[data-camp-video-choice]').forEach((item) => {
      item.setAttribute(
        'aria-pressed',
        String((item.dataset.campVideoChoice || '') === this.selectedVideoSource)
      );
    });
    const selectedVideoChoice = [...this.root.querySelectorAll('[data-camp-video-choice]')].find(
      (item) => (item.dataset.campVideoChoice || '') === this.selectedVideoSource
    );
    const videoLabel = this.root.querySelector('[data-camp-video-dropdown-label]');
    if (videoLabel) {
      videoLabel.textContent = selectedVideoChoice?.dataset.campVideoName || 'All Channels';
    }
    this.root.querySelectorAll('[data-camp-saints-cc-choice]').forEach((item) => {
      item.setAttribute(
        'aria-pressed',
        String((item.dataset.campSaintsCcChoice || '') === this.selectedSaintsCcSource)
      );
    });
    const selectedSaintsCcChoice = [
      ...this.root.querySelectorAll('[data-camp-saints-cc-choice]')
    ].find((item) => (item.dataset.campSaintsCcChoice || '') === this.selectedSaintsCcSource);
    const saintsCcLabel = this.root.querySelector('[data-camp-saints-cc-dropdown-label]');
    if (saintsCcLabel) {
      saintsCcLabel.textContent =
        selectedSaintsCcChoice?.dataset.campSaintsCcName || 'All Channels';
    }
    const subheading = this.root.querySelector('[data-camp-filter-subheading]');
    if (subheading) subheading.textContent = labels[this.activeFilter] || labels.reporter;
    const filterTime = this.root.querySelector('[data-camp-filter-time]');
    if (filterTime) filterTime.hidden = !this.isXFeedFilter();
    const reporterNavigation = this.root.querySelector('.camp-feed-nav__secondary');
    if (reporterNavigation) reporterNavigation.hidden = this.activeFilter !== 'reporter';
    const positionNavigation = this.root.querySelector('[data-camp-position-navigation]');
    if (positionNavigation) positionNavigation.hidden = this.activeFilter !== 'player';
    const videoNavigation = this.root.querySelector('[data-camp-video-navigation]');
    if (videoNavigation) videoNavigation.hidden = this.activeFilter !== 'video';
    const saintsCcNavigation = this.root.querySelector('[data-camp-saints-cc-navigation]');
    if (saintsCcNavigation) saintsCcNavigation.hidden = this.activeFilter !== 'saints-cc';
  }

  toggleReporterDropdown() {
    if (!this.reporterDropdownMenu) return;
    const willOpen = this.reporterDropdownMenu.hidden;
    this.reporterDropdownMenu.hidden = !willOpen;
    this.reporterDropdownButton?.setAttribute('aria-expanded', String(willOpen));
    if (willOpen) {
      this.reporterDropdownMenu
        .querySelector('[aria-pressed="true"]')
        ?.focus({ preventScroll: true });
    }
  }

  closeReporterDropdown() {
    if (!this.reporterDropdownMenu) return;
    this.reporterDropdownMenu.hidden = true;
    this.reporterDropdownButton?.setAttribute('aria-expanded', 'false');
  }

  selectReporter(handle = '') {
    const scrollPosition = { left: window.scrollX, top: window.scrollY };
    this.selectedReporter = String(handle).toLowerCase();
    this.activeFilter = 'reporter';
    this.visibleCount = this.batchSize;
    this.updateFilterControls();
    this.render();
    this.closeReporterDropdown();
    this.restoreDropdownViewport(this.reporterDropdownButton, scrollPosition);
  }

  togglePositionDropdown() {
    if (!this.positionDropdownMenu) return;
    const willOpen = this.positionDropdownMenu.hidden;
    this.positionDropdownMenu.hidden = !willOpen;
    this.positionDropdownButton?.setAttribute('aria-expanded', String(willOpen));
    if (willOpen) {
      this.positionDropdownMenu
        .querySelector('[aria-pressed="true"]')
        ?.focus({ preventScroll: true });
    }
  }

  closePositionDropdown() {
    if (!this.positionDropdownMenu) return;
    this.positionDropdownMenu.hidden = true;
    this.positionDropdownButton?.setAttribute('aria-expanded', 'false');
  }

  selectPosition(position = '') {
    const scrollPosition = { left: window.scrollX, top: window.scrollY };
    this.selectedPosition = String(position).toLowerCase();
    this.activeFilter = 'player';
    this.visibleCount = this.batchSize;
    this.updateFilterControls();
    this.render();
    this.closePositionDropdown();
    this.restoreDropdownViewport(this.positionDropdownButton, scrollPosition);
  }

  toggleVideoDropdown() {
    if (!this.videoDropdownMenu) return;
    const willOpen = this.videoDropdownMenu.hidden;
    this.videoDropdownMenu.hidden = !willOpen;
    this.videoDropdownButton?.setAttribute('aria-expanded', String(willOpen));
    if (willOpen) {
      this.videoDropdownMenu
        .querySelector('[aria-pressed="true"]')
        ?.focus({ preventScroll: true });
    }
  }

  closeVideoDropdown() {
    if (!this.videoDropdownMenu) return;
    this.videoDropdownMenu.hidden = true;
    this.videoDropdownButton?.setAttribute('aria-expanded', 'false');
  }

  selectVideoSource(source = '') {
    const scrollPosition = { left: window.scrollX, top: window.scrollY };
    this.selectedVideoSource = String(source).toLowerCase();
    this.activeFilter = 'video';
    this.visibleCount = this.batchSize;
    this.updateFilterControls();
    this.render();
    this.closeVideoDropdown();
    this.restoreDropdownViewport(this.videoDropdownButton, scrollPosition);
  }

  toggleSaintsCcDropdown() {
    if (!this.saintsCcDropdownMenu) return;
    const willOpen = this.saintsCcDropdownMenu.hidden;
    this.saintsCcDropdownMenu.hidden = !willOpen;
    this.saintsCcDropdownButton?.setAttribute('aria-expanded', String(willOpen));
    if (willOpen) {
      this.saintsCcDropdownMenu
        .querySelector('[aria-pressed="true"]')
        ?.focus({ preventScroll: true });
    }
  }

  closeSaintsCcDropdown() {
    if (!this.saintsCcDropdownMenu) return;
    this.saintsCcDropdownMenu.hidden = true;
    this.saintsCcDropdownButton?.setAttribute('aria-expanded', 'false');
  }

  selectSaintsCcSource(source = '') {
    const scrollPosition = { left: window.scrollX, top: window.scrollY };
    this.selectedSaintsCcSource = String(source).toLowerCase();
    this.activeFilter = 'saints-cc';
    this.visibleCount = this.batchSize;
    this.updateFilterControls();
    this.render();
    this.closeSaintsCcDropdown();
    this.restoreDropdownViewport(this.saintsCcDropdownButton, scrollPosition);
  }

  restoreDropdownViewport(trigger, scrollPosition) {
    const documentElement = document.documentElement;
    const previousScrollBehavior = documentElement.style.scrollBehavior;
    documentElement.style.scrollBehavior = 'auto';
    trigger?.focus({ preventScroll: true });
    const restore = () => window.scrollTo(scrollPosition.left, scrollPosition.top);
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(restore);
    });
    window.setTimeout(() => {
      restore();
      documentElement.style.scrollBehavior = previousScrollBehavior;
    }, 150);
  }

  setupAutoLoadMore() {
    if (!this.loadButton || !('IntersectionObserver' in window)) return;
    this.loadMoreObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || this.loadButton.hidden) return;
        this.visibleCount += this.batchSize;
        this.render();
      },
      { rootMargin: '240px 0px' }
    );
    this.loadMoreObserver.observe(this.loadButton);
  }

  async loadReporterFeeds() {
    const containers = [...this.root.querySelectorAll('[data-camp-x-feed]')];
    if (!containers.length) return;
    if (this.reporterFeedLoading) return;
    if (!this.feedApiUrl) {
      containers.forEach((container) => {
        container.textContent = 'Reporter feed API is not configured.';
      });
      return;
    }
    this.reporterFeedLoading = true;
    try {
      const response = await fetch(`${this.feedApiUrl}/api/reporters`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Feed API returned ${response.status}`);
      const payload = await response.json();
      this.updateFeedWindowStatus(payload.feed_window);
      const reporterByName = new Map(
        (payload.reporters || []).map((reporter) => [reporter.username.toLowerCase(), reporter])
      );
      containers.forEach((container) => {
        const reporter = reporterByName.get(container.dataset.screenName.toLowerCase());
        this.renderReporterPost(container, reporter);
      });
      this.sortReporterPostsByDate();
      this.updateSidebarReporters(reporterByName);
      this.updateReporterDropdown(reporterByName);
    } catch {
      containers.forEach((container) => {
        container.textContent = 'The latest post is temporarily unavailable. Use the profile link below.';
      });
    } finally {
      this.reporterFeedLoading = false;
    }
  }

  updateFeedWindowStatus(feedWindow) {
    const node = this.root.querySelector('[data-camp-filter-time]');
    if (!node || !feedWindow) return;
    if (feedWindow.emergency_paused) {
      node.textContent = 'Coverage paused';
      return;
    }
    if (!feedWindow.master_enabled) {
      node.textContent = 'Live collection disabled';
      return;
    }
    if (!feedWindow.day_enabled) {
      node.textContent = 'No live window today';
      return;
    }
    node.textContent = `${this.formatCentralTime(feedWindow.starts_at)} – ${this.formatCentralTime(
      feedWindow.ends_at
    )} Central`;
  }

  formatCentralTime(value) {
    if (!/^\d{2}:\d{2}$/.test(String(value || ''))) return value || '';
    const [hour, minute] = value.split(':').map(Number);
    return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(
      new Date(2020, 0, 1, hour, minute)
    );
  }

  startReporterAutoRefresh() {
    window.setInterval(() => {
      if (document.visibilityState === 'visible' && this.isXFeedFilter()) {
        this.loadReporterFeeds();
      }
    }, 20000);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.isXFeedFilter()) {
        this.loadReporterFeeds();
      }
    });
  }

  isXFeedFilter() {
    return this.activeFilter === 'reporter' || this.activeFilter === 'cowboys';
  }

  shouldEnableReporterAutoRefresh() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('x_live_preview') === '1') return true;

    const hostname = window.location.hostname.toLowerCase();
    const isLocalPreview = ['localhost', '127.0.0.1', '0.0.0.0'].includes(hostname);
    const isThemeEditor = Boolean(window.Shopify?.designMode);
    return !isLocalPreview && !isThemeEditor;
  }

  updateSidebarReporters(reporterByName) {
    this.root.querySelectorAll('[data-camp-sidebar-reporter]').forEach((row) => {
      const reporter = reporterByName.get(row.dataset.campSidebarReporter.toLowerCase());
      const avatar = row.querySelector('[data-camp-sidebar-avatar]');
      if (avatar && reporter?.avatar) {
        avatar.src = reporter.avatar;
        avatar.alt = `${reporter.name} profile picture`;
        avatar.hidden = false;
      }
    });
  }

  updateReporterDropdown(reporterByName) {
    this.root.querySelectorAll('[data-camp-reporter-choice]').forEach((button) => {
      const handle = button.dataset.campReporterChoice;
      if (!handle) return;
      const reporter = reporterByName.get(handle.toLowerCase());
      const avatar = button.querySelector('[data-camp-dropdown-avatar]');
      if (avatar && reporter?.avatar) {
        avatar.src = reporter.avatar;
        avatar.alt = `${reporter.name} profile picture`;
        avatar.hidden = false;
      }
    });
  }

  sortReporterPostsByDate() {
    const feed = this.root.querySelector('.camp-feed');
    if (!feed) return;

    const reporterCards = [...feed.querySelectorAll('.camp-feed-card--x')].sort((left, right) => {
      const leftTime = Date.parse(left.dataset.campPublishedAt || '') || 0;
      const rightTime = Date.parse(right.dataset.campPublishedAt || '') || 0;
      return rightTime - leftTime;
    });
    const sortedCards = document.createDocumentFragment();
    reporterCards.forEach((card) => sortedCards.appendChild(card));
    feed.prepend(sortedCards);
    this.items = [...this.root.querySelectorAll('[data-camp-category]')];
    this.render();
  }

  async toggleReporterArchive(button) {
    const panel = button.closest('.camp-feed-card')?.querySelector('[data-camp-post-archive]');
    if (!panel) return;
    if (panel.dataset.loaded === 'true') {
      panel.hidden = !panel.hidden;
      button.setAttribute('aria-expanded', String(!panel.hidden));
      button.textContent = panel.hidden ? 'Previous posts →' : 'Hide previous posts ↑';
      return;
    }

    panel.hidden = false;
    panel.textContent = 'Loading previous posts…';
    button.disabled = true;
    try {
      const currentCard = button.closest('.camp-feed-card');
      const query = new URLSearchParams({
        username: button.dataset.screenName,
        before: currentCard?.dataset.campPublishedAt || new Date().toISOString(),
        limit: '10'
      });
      const response = await fetch(`${this.feedApiUrl}/api/archive?${query}`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Archive API returned ${response.status}`);
      const payload = await response.json();
      this.renderReporterArchive(panel, payload.posts || []);
      panel.dataset.loaded = 'true';
      button.setAttribute('aria-expanded', 'true');
      button.textContent = 'Hide previous posts ↑';
    } catch {
      panel.textContent = 'Previous posts are temporarily unavailable.';
    } finally {
      button.disabled = false;
    }
  }

  renderReporterArchive(panel, posts) {
    panel.replaceChildren();
    if (!posts.length) {
      const empty = document.createElement('p');
      empty.className = 'camp-post-archive__empty';
      empty.textContent = 'No previous posts have been archived yet.';
      panel.appendChild(empty);
      return;
    }

    posts.forEach((post) => {
      const item = document.createElement('article');
      item.className = 'camp-post-archive__item';
      const time = document.createElement('time');
      time.dateTime = post.created_at || '';
      time.textContent = post.created_at
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date(post.created_at)
          )
        : 'Previous post';
      const text = document.createElement('p');
      text.textContent = String(post.text || '')
        .replace(/https?:\/\/\S+/gi, '')
        .replace(/[ \t]{2,}/g, ' ')
        .trim();
      const mediaItems = (post.media || []).filter((media) => media.url);
      const mediaGrid = document.createElement('div');
      mediaGrid.className = 'camp-post-archive__media-grid';
      mediaItems.forEach((media) => {
        if ((media.type === 'video' || media.type === 'animated_gif') && media.playback_url) {
          const video = document.createElement('video');
          video.className = 'camp-post-archive__media camp-post-archive__video';
          video.controls = true;
          video.preload = 'metadata';
          video.playsInline = true;
          video.poster = media.url;
          video.src = `${this.feedApiUrl}/api/x-video?url=${encodeURIComponent(media.playback_url)}`;
          video.setAttribute('aria-label', media.alt || 'Video attached to this archived X post');
          video.addEventListener(
            'error',
            () => {
              const fallback = document.createElement('a');
              fallback.className = 'camp-post-archive__media-fallback';
              fallback.href = post.url;
              fallback.target = '_blank';
              fallback.rel = 'noopener';
              fallback.textContent = 'Play archived video on X →';
              video.after(fallback);
            },
            { once: true }
          );
          mediaGrid.appendChild(video);
          return;
        }

        const image = document.createElement('img');
        image.className = 'camp-post-archive__media';
        image.src = media.url;
        image.alt = media.alt || '';
        image.loading = 'lazy';
        if (media.width) image.width = media.width;
        if (media.height) image.height = media.height;
        mediaGrid.appendChild(image);
      });
      const link = document.createElement('a');
      link.href = post.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'View archived post on X →';
      item.append(time, text);
      if (mediaItems.length) item.appendChild(mediaGrid);
      item.appendChild(link);
      panel.appendChild(item);
    });
  }

  async loadCampVideos() {
    const container = this.root.querySelector('[data-camp-youtube-videos]');
    const fallback = this.root.querySelector('[data-camp-video-fallback]');
    if (!container || !this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/videos`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Video API returned ${response.status}`);
      const payload = await response.json();
      const limit = Number.parseInt(container.dataset.videoCount, 10) || 4;
      const videos = (payload.videos || []).slice(0, limit);
      if (!videos.length) throw new Error('No matching videos were returned');

      container.replaceChildren();
      videos.forEach((video) => container.appendChild(this.createVideoCard(video)));
      this.populateVideoSources(payload.videos || []);
      this.renderFeedVideos(payload.videos || []);
      if (fallback) fallback.hidden = true;
    } catch {
      container.hidden = true;
      if (fallback) fallback.hidden = false;
    }
  }

  async loadSaintsCcVideos() {
    if (!this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/saints-cc-videos`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Saints CC video API returned ${response.status}`);
      const payload = await response.json();
      const videos = payload.videos || [];
      if (!videos.length) throw new Error('No Saints CC videos were returned');
      const latestByChannel = new Map();
      videos.forEach((video) => {
        const source = this.videoSourceKey(video);
        if (!latestByChannel.has(source)) latestByChannel.set(source, video);
      });
      this.saintsCcAllChannelLimit = latestByChannel.size;
      const byNewestPublished = (left, right) =>
        new Date(right.published_at || 0) - new Date(left.published_at || 0);
      const featuredVideos = [...latestByChannel.values()].sort(byNewestPublished);
      const featuredIds = new Set([...latestByChannel.values()].map((video) => video.id));
      const orderedVideos = [
        ...featuredVideos,
        ...videos.filter((video) => !featuredIds.has(video.id)).sort(byNewestPublished)
      ];
      this.populateSaintsCcSources(payload.channels || [], videos);
      this.populateSidebarSaintsCc(payload.channels || []);
      this.renderFeedVideos(orderedVideos, 'saints-cc');
    } catch {
      // Leave the Saints CC filter empty if its dedicated feed is unavailable.
    }
  }

  async loadCampBuzz() {
    const container = this.root.querySelector('[data-camp-buzz]');
    if (!container || !this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/camp-buzz`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Camp Buzz API returned ${response.status}`);
      const payload = await response.json();
      const articles = payload.articles || [];
      if (!articles.length) throw new Error('No current camp articles were returned');

      container.replaceChildren();
      articles.forEach((article) => {
        const link = document.createElement('a');
        link.className = 'camp-buzz__article';
        link.href = article.url;
        link.target = '_blank';
        link.rel = 'noopener';

        const title = document.createElement('h4');
        title.textContent = article.title;
        const excerpt = document.createElement('p');
        excerpt.textContent = article.excerpt;
        const meta = document.createElement('span');
        const published = article.published_at
          ? new Intl.DateTimeFormat('en-US', {
              timeZone: 'America/Chicago',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              timeZoneName: 'short'
            }).format(new Date(article.published_at))
          : '';
        meta.textContent = [article.author, published].filter(Boolean).join(' · ');
        link.append(title, excerpt, meta);
        container.appendChild(link);
      });
    } catch {
      container.innerHTML = '<p class="camp-buzz__status">Current camp articles are temporarily unavailable.</p>';
    }
  }

  async loadCampToday() {
    const card = this.root.querySelector('[data-camp-today]');
    const mobilePracticeDate = this.root.querySelector('[data-camp-mobile-practice-date]');
    const mobilePracticeTime = this.root.querySelector('[data-camp-mobile-practice-time]');
    const mobileTemperature = this.root.querySelector('[data-camp-mobile-temperature]');
    if ((!card && !mobilePracticeDate) || !this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/camp-today`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Today at Camp API returned ${response.status}`);
      const payload = await response.json();
      const event = payload.event;
      const eventName = card?.querySelector('[data-camp-today-event]');
      const eventTime = card?.querySelector('[data-camp-today-time]');
      const practiceDate = card?.querySelector('[data-camp-next-practice-date]');
      const location = card?.querySelector('[data-camp-today-location]');
      const fanStatus = card?.querySelector('[data-camp-today-fan-status]');
      const weather = card?.querySelector('[data-camp-today-weather]');
      const schedule = card?.querySelector('[data-camp-today-schedule]');

      if (eventName) eventName.textContent = event?.name || 'No scheduled practice';
      if (eventTime) eventTime.textContent = event?.time || 'See the complete camp schedule';
      if (practiceDate) {
        practiceDate.textContent = event?.date
          ? this.formatPracticeDate(event.date)
          : 'Schedule TBD';
      }
      if (location) location.textContent = event?.location || 'Location not listed';
      const apiFanAccess =
        event?.access ??
        event?.open_to_public ??
        event?.is_public ??
        event?.public_access ??
        event?.fan_status;
      if (fanStatus && apiFanAccess !== undefined && apiFanAccess !== null) {
        const normalizedAccess =
          typeof apiFanAccess === 'boolean'
            ? apiFanAccess
              ? 'open'
              : 'closed'
            : /closed|private|not open/i.test(String(apiFanAccess))
              ? 'closed'
              : 'open';
        const fanStatusLabel = fanStatus.querySelector('[data-camp-today-fan-status-label]');
        fanStatus.classList.toggle('camp-today__fan-status--open', normalizedAccess === 'open');
        fanStatus.classList.toggle('camp-today__fan-status--closed', normalizedAccess === 'closed');
        if (fanStatusLabel) {
          fanStatusLabel.textContent = normalizedAccess === 'open' ? 'Open to public' : 'Closed to public';
        }
      }
      if (weather && payload.weather) {
        const details = [
          Number.isFinite(payload.weather.temperature) ? `${payload.weather.temperature}°F` : '',
          payload.weather.condition,
          Number.isFinite(payload.weather.feels_like)
            ? `Feels like ${payload.weather.feels_like}°F`
            : ''
        ].filter(Boolean);
        weather.textContent = details.join(' · ');
        weather.href = payload.weather.source_url || weather.href;
      }
      if (schedule) schedule.href = payload.schedule_url || schedule.href;
      if (mobilePracticeDate) {
        mobilePracticeDate.textContent = event?.date
          ? this.formatPracticeDate(event.date)
          : 'Schedule TBD';
      }
      if (mobilePracticeTime) {
        mobilePracticeTime.textContent = event?.time || 'See schedule';
      }
      if (mobileTemperature) {
        mobileTemperature.textContent = Number.isFinite(payload.weather?.temperature)
          ? `${Math.round(payload.weather.temperature)}°`
          : '--°';
      }
    } catch {
      // Keep the theme-editor fallback values if either live source is unavailable.
    }
  }

  formatPracticeDate(value) {
    const date = new Date(`${value}T12:00:00`);
    if (Number.isNaN(date.valueOf())) return value;
    const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'long' }).format(date);
    const month = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
    const day = date.getDate();
    const remainder = day % 100;
    const suffix =
      remainder >= 11 && remainder <= 13
        ? 'th'
        : day % 10 === 1
          ? 'st'
          : day % 10 === 2
            ? 'nd'
            : day % 10 === 3
              ? 'rd'
              : 'th';
    return `${weekday} ${month} ${day}${suffix}`;
  }

  createVideoCard(video) {
    const card = document.createElement('article');
    card.className = 'camp-video camp-video--automatic';

    const player = document.createElement('div');
    player.className = 'camp-video__player';
    const button = document.createElement('button');
    button.className = 'camp-video__play';
    button.type = 'button';
    button.setAttribute('aria-label', `Play ${video.title}`);

    const image = document.createElement('img');
    image.src = video.thumbnail;
    image.alt = '';
    image.loading = 'lazy';
    const playIcon = document.createElement('span');
    playIcon.className = 'camp-video__play-icon';
    playIcon.textContent = '▶';
    button.append(image, playIcon);
    button.addEventListener('click', () => {
      const iframe = document.createElement('iframe');
      iframe.src = `${video.embed_url}?autoplay=1`;
      iframe.title = video.title;
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      iframe.allowFullscreen = true;
      player.replaceChildren(iframe);
    });
    player.appendChild(button);

    const title = document.createElement('strong');
    title.textContent = video.title;
    const channel = document.createElement('span');
    channel.className = 'camp-video__channel';
    channel.textContent = video.channel_title || 'YouTube';
    card.append(player, channel, title);
    return card;
  }

  renderFeedVideos(videos, category = 'video') {
    const feed = this.root.querySelector('.camp-feed');
    if (!feed) return;

    const cardAttribute =
      category === 'saints-cc' ? 'data-camp-saints-cc-feed-card' : 'data-camp-youtube-feed-card';
    feed.querySelectorAll(`[${cardAttribute}]`).forEach((card) => card.remove());
    const cards = videos.map((video) => {
      const card = document.createElement('article');
      card.className = 'camp-card camp-feed-card camp-feed-video';
      card.dataset.campCategory = category;
      if (category === 'saints-cc') {
        card.dataset.campSaintsCcFeedCard = '';
      } else {
        card.dataset.campYoutubeFeedCard = '';
      }
      card.dataset.campVideoSource = this.videoSourceKey(video);

      const header = document.createElement('header');
      header.className = 'camp-feed-card__header camp-feed-video__header';
      const channelLink = document.createElement('a');
      channelLink.className = 'camp-feed-video__channel-link';
      channelLink.href = video.channel_url || '#';
      channelLink.target = '_blank';
      channelLink.rel = 'noopener';
      channelLink.setAttribute('aria-label', `View ${video.channel_title || 'channel'} on YouTube`);
      if (video.channel_avatar) {
        const avatar = document.createElement('img');
        avatar.className = 'camp-feed-video__avatar';
        avatar.src = video.channel_avatar;
        avatar.alt = `${video.channel_title || 'YouTube channel'} profile picture`;
        avatar.loading = 'lazy';
        avatar.width = 40;
        avatar.height = 40;
        channelLink.appendChild(avatar);
      }
      const source = document.createElement('div');
      const name = document.createElement('p');
      name.className = 'camp-feed-card__source';
      name.textContent = video.channel_title || 'Boot Krewe Media';
      const date = document.createElement('span');
      date.className = 'camp-feed-card__handle';
      date.textContent = video.published_at
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(video.published_at))
        : 'Saints Training Camp';
      source.append(name, date);
      const platform = document.createElement('span');
      platform.className = 'camp-feed-card__platform';
      platform.textContent = 'YouTube';
      header.append(channelLink, source, platform);

      const playerCard = this.createVideoCard(video);
      const link = document.createElement('a');
      link.className = 'camp-feed-card__link';
      link.href = video.watch_url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = 'Watch on YouTube →';
      card.append(header, playerCard, link);
      return card;
    });

    cards.forEach((card) => feed.appendChild(card));
    this.items = [...this.root.querySelectorAll('[data-camp-category]')];
    this.render();
  }

  videoSourceKey(video) {
    return String(video.channel_handle || video.channel_title || 'youtube').toLowerCase();
  }

  populateVideoSources(videos) {
    if (!this.videoDropdownMenu) return;
    this.videoDropdownMenu
      .querySelectorAll('[data-camp-video-choice]:not([data-camp-video-choice=""])')
      .forEach((item) => item.remove());

    const sources = new Map();
    videos.forEach((video) => {
      const key = this.videoSourceKey(video);
      if (!sources.has(key)) {
        sources.set(key, {
          name: video.channel_title || video.channel_handle || 'YouTube',
          avatar: video.channel_avatar || ''
        });
      }
    });

    [...sources.entries()]
      .sort((left, right) => left[1].name.localeCompare(right[1].name))
      .forEach(([key, source]) => {
        const button = document.createElement('button');
        button.className = 'camp-video-choice camp-video-choice--channel';
        button.type = 'button';
        button.dataset.campVideoChoice = key;
        button.dataset.campVideoName = source.name;
        button.setAttribute('aria-pressed', String(key === this.selectedVideoSource));
        button.setAttribute('role', 'option');

        const avatar = document.createElement('img');
        avatar.className = 'camp-video-choice__avatar';
        avatar.src = source.avatar;
        avatar.alt = '';
        avatar.loading = 'lazy';
        avatar.width = 32;
        avatar.height = 32;
        const label = document.createElement('span');
        label.textContent = source.name;
        const check = document.createElement('span');
        check.className = 'camp-video-choice__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        button.append(avatar, label, check);
        this.videoDropdownMenu.appendChild(button);
      });
    this.updateFilterControls();
  }

  populateSaintsCcSources(channels, videos) {
    if (!this.saintsCcDropdownMenu) return;
    this.saintsCcDropdownMenu
      .querySelectorAll('[data-camp-saints-cc-choice]:not([data-camp-saints-cc-choice=""])')
      .forEach((item) => item.remove());

    const videoBySource = new Map(
      videos.map((video) => [
        this.videoSourceKey(video),
        {
          name: video.channel_title || video.channel_handle || 'YouTube',
          avatar: video.channel_avatar || ''
        }
      ])
    );
    const saintsCcChannelOrder = this.saintsCcChannelOrder();
    channels
      .map((channel) => {
        const key = String(channel.handle || channel.title || '').toLowerCase();
        return [
          key,
          {
            name: channel.title || channel.handle || 'YouTube',
            avatar: channel.avatar || videoBySource.get(key)?.avatar || ''
          }
        ];
      })
      .sort((left, right) => {
        const leftIndex = saintsCcChannelOrder.indexOf(left[0]);
        const rightIndex = saintsCcChannelOrder.indexOf(right[0]);
        if (leftIndex === -1 && rightIndex === -1) return left[1].name.localeCompare(right[1].name);
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
      })
      .forEach(([key, source]) => {
        const button = document.createElement('button');
        button.className = 'camp-video-choice camp-video-choice--channel';
        button.type = 'button';
        button.dataset.campSaintsCcChoice = key;
        button.dataset.campSaintsCcName = source.name;
        button.setAttribute('aria-pressed', String(key === this.selectedSaintsCcSource));
        button.setAttribute('role', 'option');

        const avatar = document.createElement('img');
        avatar.className = 'camp-video-choice__avatar';
        avatar.src = source.avatar;
        avatar.alt = '';
        avatar.loading = 'lazy';
        avatar.width = 32;
        avatar.height = 32;
        const label = document.createElement('span');
        label.textContent = source.name;
        const check = document.createElement('span');
        check.className = 'camp-video-choice__check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        button.append(avatar, label, check);
        this.saintsCcDropdownMenu.appendChild(button);
      });
    this.updateFilterControls();
  }

  saintsCcChannelOrder() {
    return [
      '@thesportscomawithbigq',
      '@jamesskrmetta',
      '@jonobarnes',
      '@lockedonsaints',
      '@straightuptjay',
      '@snowlikejon',
      '@afrsaints',
      '@offthebenchsaints',
      '@nof'
    ];
  }

  populateSidebarSaintsCc(channels) {
    const container = this.root.querySelector('[data-camp-sidebar-saints-cc]');
    if (!container) return;
    const order = this.saintsCcChannelOrder();
    const sortedChannels = [...channels].sort((left, right) => {
      const leftKey = String(left.handle || '').toLowerCase();
      const rightKey = String(right.handle || '').toLowerCase();
      const leftIndex = order.indexOf(leftKey);
      const rightIndex = order.indexOf(rightKey);
      if (leftIndex === -1 && rightIndex === -1) {
        return String(left.title || '').localeCompare(String(right.title || ''));
      }
      if (leftIndex === -1) return 1;
      if (rightIndex === -1) return -1;
      return leftIndex - rightIndex;
    });

    container.replaceChildren();
    sortedChannels.forEach((channel) => {
      const row = document.createElement('div');
      row.className = 'camp-reporter camp-creator';
      const identity = document.createElement('a');
      identity.className = 'camp-reporter__identity';
      identity.href = channel.url;
      identity.target = '_blank';
      identity.rel = 'noopener';
      identity.setAttribute('aria-label', `View ${channel.title || channel.handle} on YouTube`);

      if (channel.avatar) {
        const avatar = document.createElement('img');
        avatar.src = channel.avatar;
        avatar.alt = `${channel.title || channel.handle} profile picture`;
        avatar.width = 38;
        avatar.height = 38;
        avatar.loading = 'lazy';
        identity.appendChild(avatar);
      }
      const copy = document.createElement('div');
      const name = document.createElement('strong');
      name.textContent = channel.title || channel.handle || 'Saints creator';
      const handle = document.createElement('small');
      handle.textContent = channel.handle || 'YouTube';
      copy.append(name, handle);
      identity.appendChild(copy);

      const viewLink = document.createElement('a');
      viewLink.className = 'camp-reporter__follow';
      viewLink.href = channel.url;
      viewLink.target = '_blank';
      viewLink.rel = 'noopener';
      viewLink.textContent = 'View';
      row.append(identity, viewLink);
      container.appendChild(row);
    });
  }

  async loadSaintsRoster() {
    const feed = this.root.querySelector('.camp-feed');
    if (!feed || !this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/roster`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Roster API returned ${response.status}`);
      const payload = await response.json();
      if (!(payload.players || []).length) throw new Error('No roster players were returned');

      feed.querySelectorAll('[data-camp-roster-card]').forEach((card) => card.remove());
      const sourceCard = document.createElement('article');
      sourceCard.className = 'camp-card camp-feed-card camp-roster-source';
      sourceCard.dataset.campCategory = 'player';
      sourceCard.dataset.campRosterCard = '';
      sourceCard.dataset.campRosterSource = '';
      const sourceHeading = document.createElement('strong');
      sourceHeading.textContent = 'Official New Orleans Saints Roster';
      const sourceText = document.createElement('span');
      sourceText.textContent = `Updated ${new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(payload.generated_at))}`;
      const sourceLink = document.createElement('a');
      sourceLink.href = payload.source?.url || 'https://www.neworleanssaints.com/team/players-roster/';
      sourceLink.target = '_blank';
      sourceLink.rel = 'noopener';
      sourceLink.textContent = 'View official roster →';
      sourceCard.append(sourceHeading, sourceText, sourceLink);
      feed.appendChild(sourceCard);

      payload.players.forEach((player) => feed.appendChild(this.createRosterCard(player)));
      this.populatePositionDropdown(payload.players);
      this.items = [...this.root.querySelectorAll('[data-camp-category]')];
      this.render();
    } catch {
      // Keep the existing manually configured player content as a fallback.
    }
  }

  createRosterCard(player) {
    const card = document.createElement('article');
    card.className = 'camp-card camp-feed-card camp-player-card';
    card.dataset.campCategory = 'player';
    card.dataset.campRosterCard = '';
    card.dataset.campPlayerPosition = String(player.position || '').toLowerCase();

    const link = document.createElement('a');
    link.className = 'camp-player-card__identity';
    link.href = player.profile_url;
    link.target = '_blank';
    link.rel = 'noopener';
    if (player.image) {
      const image = document.createElement('img');
      image.src = player.image;
      image.alt = `${player.name} headshot`;
      image.loading = 'lazy';
      image.width = 92;
      image.height = 92;
      link.appendChild(image);
    }
    const identity = document.createElement('span');
    const name = document.createElement('strong');
    name.textContent = player.name;
    const position = document.createElement('span');
    position.textContent = `#${player.number || '—'} · ${player.position || '—'}`;
    identity.append(name, position);
    link.appendChild(identity);

    const details = document.createElement('dl');
    details.className = 'camp-player-card__details';
    [
      ['Status', player.status],
      ['HT / WT', `${player.height || '—'} / ${player.weight || '—'} lbs`],
      ['Age', player.age],
      ['Experience', player.experience],
      ['College', player.college]
    ].forEach(([label, value]) => {
      const group = document.createElement('div');
      const term = document.createElement('dt');
      term.textContent = label;
      const description = document.createElement('dd');
      description.textContent = value || '—';
      group.append(term, description);
      details.appendChild(group);
    });

    card.append(link, details);
    return card;
  }

  populatePositionDropdown(players) {
    if (!this.positionDropdownMenu) return;
    this.positionDropdownMenu
      .querySelectorAll('[data-camp-position-choice]:not([data-camp-position-choice=""])')
      .forEach((button) => button.remove());
    const positionOrder = [
      'QB',
      'RB',
      'WR',
      'TE',
      'T',
      'G',
      'C',
      'FB',
      'OL',
      'DE',
      'DT',
      'DL',
      'LB',
      'CB',
      'S',
      'DB',
      'K',
      'P',
      'LS'
    ];
    const positions = [...new Set(players.map((player) => String(player.position || '').trim()).filter(Boolean))]
      .sort((left, right) => {
        const leftIndex = positionOrder.indexOf(left.toUpperCase());
        const rightIndex = positionOrder.indexOf(right.toUpperCase());
        if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
      });
    positions.forEach((position) => {
      const button = document.createElement('button');
      button.className = 'camp-position-choice';
      button.type = 'button';
      button.dataset.campPositionChoice = position.toLowerCase();
      button.dataset.campPositionName = position;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('role', 'option');
      const label = document.createElement('span');
      label.textContent = position;
      const check = document.createElement('span');
      check.className = 'camp-position-choice__check';
      check.setAttribute('aria-hidden', 'true');
      check.textContent = '✓';
      button.append(label, check);
      this.positionDropdownMenu.appendChild(button);
    });
    this.updateFilterControls();
  }

  async loadCampPhotos() {
    const feed = this.root.querySelector('.camp-feed');
    if (!feed || !this.feedApiUrl) return;

    try {
      const response = await fetch(`${this.feedApiUrl}/api/photos`, {
        headers: { accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Photo API returned ${response.status}`);
      const payload = await response.json();
      if (!(payload.photos || []).length) throw new Error('No matching camp photos were returned');

      feed.querySelectorAll('[data-camp-official-photo]').forEach((card) => card.remove());
      payload.photos.forEach((photo) => feed.appendChild(this.createCampPhotoCard(photo)));
      this.items = [...this.root.querySelectorAll('[data-camp-category]')];
      this.render();
    } catch {
      // Keep manually configured photo posts as a fallback.
    }
  }

  createCampPhotoCard(photo) {
    const card = document.createElement('article');
    card.className = 'camp-card camp-feed-card camp-photo-card';
    card.dataset.campCategory = 'photo';
    card.dataset.campOfficialPhoto = '';

    const header = document.createElement('header');
    header.className = 'camp-feed-card__header camp-photo-card__header';
    const source = document.createElement('div');
    const name = document.createElement('p');
    name.className = 'camp-feed-card__source';
    name.textContent = 'New Orleans Saints';
    const meta = document.createElement('span');
    meta.className = 'camp-feed-card__handle';
    const date = photo.published_at
      ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(photo.published_at))
      : 'Saints Training Camp';
    meta.textContent = photo.creator ? `${photo.creator} · ${date}` : date;
    source.append(name, meta);
    const label = document.createElement('span');
    label.className = 'camp-feed-card__platform';
    label.textContent = 'Photos';
    header.append(source, label);

    const link = document.createElement('a');
    link.className = 'camp-photo-card__link';
    link.href = photo.url;
    link.target = '_blank';
    link.rel = 'noopener';
    const image = document.createElement('img');
    image.className = 'camp-photo-card__image';
    image.src = photo.image;
    image.alt = photo.title;
    image.loading = 'lazy';
    image.width = 300;
    image.height = 300;
    const title = document.createElement('strong');
    title.textContent = photo.title;
    link.append(image, title);
    card.append(header, link);
    return card;
  }

  renderReporterPost(container, reporter) {
    container.replaceChildren();
    const feedCard = container.closest('.camp-feed-card');
    const avatar = feedCard?.querySelector('[data-camp-x-avatar]');
    const timestamp = feedCard?.querySelector('[data-camp-x-timestamp]');
    if (avatar && reporter?.avatar) {
      avatar.src = reporter.avatar;
      avatar.alt = `${reporter.name} profile picture`;
      avatar.hidden = false;
    }
    if (!reporter?.post) {
      if (feedCard) feedCard.dataset.campPublishedAt = '';
      if (timestamp) {
        timestamp.dateTime = '';
        timestamp.textContent = '';
        timestamp.hidden = true;
      }
      container.textContent = 'No recent public post was returned.';
      return;
    }
    if (feedCard) feedCard.dataset.campPublishedAt = reporter.post.created_at || '';
    if (timestamp) {
      timestamp.dateTime = reporter.post.created_at || '';
      timestamp.textContent = reporter.post.created_at
        ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
            new Date(reporter.post.created_at)
          )
        : 'Latest update';
      timestamp.hidden = false;
    }

    const post = document.createElement('article');
    post.className = 'camp-native-post';

    const text = document.createElement('p');
    text.className = 'camp-native-post__text';
    text.textContent = reporter.post.text
      .replace(/https?:\/\/\S+/gi, '')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
    post.appendChild(text);

    const mediaItems = (reporter.post.media || []).filter((media) => media.url);
    if (mediaItems.length) {
      const mediaGrid = document.createElement('div');
      mediaGrid.className = 'camp-native-post__media-grid';
      mediaItems.forEach((media) => {
        if ((media.type === 'video' || media.type === 'animated_gif') && media.playback_url) {
          const video = document.createElement('video');
          video.className = 'camp-native-post__media camp-native-post__video';
          video.controls = true;
          video.preload = 'auto';
          video.playsInline = true;
          video.poster = media.url;
          video.src = `${this.feedApiUrl}/api/x-video?url=${encodeURIComponent(media.playback_url)}`;
          video.setAttribute('aria-label', media.alt || 'Video attached to this X post');
          video.addEventListener(
            'error',
            () => {
              const fallback = document.createElement('a');
              fallback.className = 'camp-native-post__video-fallback';
              fallback.href = reporter.post.url;
              fallback.target = '_blank';
              fallback.rel = 'noopener';
              fallback.textContent = 'Play this video on X →';
              video.after(fallback);
            },
            { once: true }
          );
          mediaGrid.appendChild(video);
          video.load();
          return;
        }

        const image = document.createElement('img');
        image.className = 'camp-native-post__media';
        image.src = media.url;
        image.alt = media.alt || '';
        image.loading = 'lazy';
        if (media.width) image.width = media.width;
        if (media.height) image.height = media.height;
        mediaGrid.appendChild(image);
      });
      post.appendChild(mediaGrid);
    }

    const actions = document.createElement('footer');
    actions.className = 'camp-native-post__actions';
    const metrics = document.createElement('div');
    metrics.className = 'camp-native-post__metrics';
    metrics.append(
      this.createXMetric('comments', reporter.post.metrics?.replies, this.xMetricIcons.comments),
      this.createXMetric('reposts', reporter.post.metrics?.reposts, this.xMetricIcons.reposts),
      this.createXMetric('likes', reporter.post.metrics?.likes, this.xMetricIcons.likes)
    );

    const viewLink = document.createElement('a');
    viewLink.className = 'camp-native-post__view-link';
    viewLink.href = reporter.post.url;
    viewLink.target = '_blank';
    viewLink.rel = 'noopener';
    viewLink.textContent = 'View on X →';
    actions.append(metrics, viewLink);
    post.appendChild(actions);

    container.appendChild(post);
  }

  createXMetric(label, value, iconUrl) {
    const metric = document.createElement('span');
    metric.className = 'camp-native-post__metric';
    const hasValue = Number.isFinite(Number(value)) && value !== null;
    const count = hasValue
      ? new Intl.NumberFormat(undefined, {
          notation: Number(value) >= 1000 ? 'compact' : 'standard',
          maximumFractionDigits: 1
        }).format(Number(value))
      : '—';
    metric.setAttribute('aria-label', hasValue ? `${count} ${label}` : `${label} unavailable`);

    const icon = document.createElement('img');
    icon.src = iconUrl;
    icon.alt = '';
    icon.width = 18;
    icon.height = 18;
    const countNode = document.createElement('span');
    countNode.textContent = count;
    metric.append(icon, countNode);
    return metric;
  }

  startCountdowns() {
    this.root.querySelectorAll('[data-camp-countdown]').forEach((node) => {
      const target = new Date(node.dataset.campCountdown).getTime();
      if (!Number.isFinite(target)) return;
      const update = () => {
        const remaining = Math.max(0, target - Date.now());
        const hours = Math.floor(remaining / 3600000);
        const minutes = Math.floor((remaining % 3600000) / 60000);
        node.textContent = remaining ? `${hours}h ${minutes}m` : 'Starting soon';
      };
      update();
      window.setInterval(update, 60000);
    });
  }

  setupStickyTabs() {
    const tabs = this.root.querySelector('.camp-tabs');
    if (!tabs) return;

    const placeholder = document.createElement('div');
    placeholder.className = 'camp-tabs__placeholder';
    tabs.before(placeholder);

    let triggerTop = 0;
    let stickyTop = 0;

    const measure = () => {
      tabs.classList.remove('camp-tabs--fixed');
      placeholder.classList.remove('camp-tabs__placeholder--active');
      stickyTop = this.root.querySelector('#header-component')?.getBoundingClientRect().height || 0;
      triggerTop = tabs.getBoundingClientRect().top + window.scrollY - stickyTop;
      placeholder.style.height = `${tabs.offsetHeight}px`;
      update();
    };

    const update = () => {
      const shouldFix = window.scrollY >= triggerTop;
      tabs.style.setProperty('--camp-sticky-top', `${stickyTop}px`);
      tabs.classList.toggle('camp-tabs--fixed', shouldFix);
      placeholder.classList.toggle('camp-tabs__placeholder--active', shouldFix);
    };

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', measure);
    measure();
  }
}

if (!document.documentElement.dataset.campInitialized) {
  document.documentElement.dataset.campInitialized = 'true';
  new SaintsCampPage(document);
}
